import React, { useState, useEffect } from 'react';

function MobileNotifications({ onBack }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  // Sample notifications — replace with API when ready
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setNotifications([
        {
          id: 1, type: 'access', read: false,
          title: 'Access Request',
          message: 'Dr. Rajesh Kumar wants to access your medical records.',
          time: '2 mins ago',
          icon: 'person_add', color: '#0B3C95', bg: '#EAF2FF'
        },
        {
          id: 2, type: 'report', read: false,
          title: 'Report Uploaded',
          message: 'Apollo Hospital uploaded a Blood Test report to your account.',
          time: '1 hour ago',
          icon: 'description', color: '#00A884', bg: '#E6F7F4'
        },
        {
          id: 3, type: 'ai', read: true,
          title: 'AI Summary Ready',
          message: 'Your health summary has been generated. Tap to view insights.',
          time: '3 hours ago',
          icon: 'auto_awesome', color: '#8B5CF6', bg: '#F0EBFB'
        },
        {
          id: 4, type: 'access', read: true,
          title: 'Access Approved',
          message: 'You approved Dr. Priya Sharma to view your records.',
          time: 'Yesterday',
          icon: 'check_circle', color: '#00A884', bg: '#E6F7F4'
        },
        {
          id: 5, type: 'report', read: true,
          title: 'Prescription Scanned',
          message: 'AI successfully extracted 3 medicines from your prescription.',
          time: 'Yesterday',
          icon: 'document_scanner', color: '#0B3C95', bg: '#EAF2FF'
        },
        {
          id: 6, type: 'system', read: true,
          title: 'Welcome to MyHealth Journal',
          message: 'Your account is set up. Start by uploading your first report!',
          time: '2 days ago',
          icon: 'medical_services', color: '#0B3C95', bg: '#EAF2FF'
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const filtered = filter === 'all'
    ? notifications
    : filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#F4F7FC]" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-full">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <h1 className="font-black text-[#0D1B2A] text-lg">Notifications</h1>
            {unreadCount > 0 && <p className="text-xs text-gray-400">{unreadCount} unread</p>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="text-xs text-[#0B3C95] font-bold px-3 py-1.5 bg-[#0B3C95]/10 rounded-full">
            Mark all read
          </button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: 'Unread' },
            { id: 'access', label: 'Access' },
            { id: 'report', label: 'Reports' },
            { id: 'ai', label: 'AI' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setFilter(tab.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                filter === tab.id
                  ? 'bg-[#0B3C95] text-white shadow-md'
                  : 'bg-white text-gray-500 border border-gray-200'
              }`}>
              {tab.label}
              {tab.id === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center py-12">
            <div className="w-8 h-8 border-2 border-[#0B3C95] border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-gray-400 text-sm">Loading notifications...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-gray-300" style={{ fontSize: '36px', fontVariationSettings: '"FILL" 1' }}>notifications_off</span>
            </div>
            <p className="font-bold text-gray-400 text-base">No notifications</p>
            <p className="text-gray-300 text-sm mt-1">You're all caught up!</p>
          </div>
        )}

        {/* Notifications list */}
        <div className="space-y-2">
          {filtered.map(notif => (
            <div key={notif.id}
              onClick={() => markRead(notif.id)}
              className={`bg-white rounded-2xl border shadow-sm p-4 flex items-start gap-3 cursor-pointer active:scale-[0.99] transition-all ${
                !notif.read ? 'border-[#0B3C95]/20 bg-[#0B3C95]/[0.02]' : 'border-gray-100'
              }`}>

              {/* Icon */}
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: notif.bg }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: notif.color, fontVariationSettings: '"FILL" 1' }}>
                  {notif.icon}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm leading-tight ${!notif.read ? 'font-black text-[#0D1B2A]' : 'font-bold text-gray-600'}`}>
                    {notif.title}
                  </p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!notif.read && <div className="w-2 h-2 bg-[#0B3C95] rounded-full"></div>}
                    <button onClick={e => { e.stopPropagation(); deleteNotification(notif.id); }}
                      className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 rounded-full">
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{notif.message}</p>
                <p className="text-[10px] text-gray-300 font-semibold mt-1.5">{notif.time}</p>
              </div>
            </div>
          ))}
        </div>

        {notifications.length > 0 && filtered.length > 0 && (
          <p className="text-center text-xs text-gray-300 pb-4">
            Showing {filtered.length} notification{filtered.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}

export default MobileNotifications;